import { List, Typography } from "@mui/material";
import { format } from "date-fns";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Nullable } from "../../../common/types/nullable";
import { fetchMoreHrTasks } from "../slice/dashboardActions";
import { LIST_HEIGHT } from "./constants";
import HrTaskListItem from "./HrTaskListItem";
import LoadMoreButton from "./LoadMoreButton";
import { HrTask } from "./types";

type Props = {
  isLoading: boolean;
  tasks: HrTask[];
  selectedHrTaskId: Nullable<string>;
  handleSelectHrTask: (taskId: string) => void;
};

const HrTasklist = ({
  isLoading,
  tasks,
  selectedHrTaskId,
  handleSelectHrTask,
}: Props) => {
  const { t } = useTranslation("dashboard");

  const { cursor } = useAppSelector((state) => state.dashboard.hrTasklist);

  const dispatch = useAppDispatch();

  const handleFetchNext = useCallback(() => {
    const params = {
      atDate: format(new Date(), "yyyy-MM-dd"),
      pageSize: 50,
      cursor: cursor as string,
    };

    dispatch(fetchMoreHrTasks(params));
  }, [cursor, dispatch]);

  useEffect(() => {
    // if next page is available and task list is empty, fetch next page
    if (cursor && !tasks.length) {
      handleFetchNext();
    }
  }, [cursor, handleFetchNext, tasks.length]);

  const showEmptyState = !isLoading && !tasks.length;
  const emptyStateListStyles = showEmptyState
    ? {
        bgcolor: "#fff",
        borderRadius: "8px",
      }
    : {};

  return (
    <List
      disablePadding
      sx={{
        height: LIST_HEIGHT,
        maxHeight: "758px",
        overflow: "auto",
        ...emptyStateListStyles,
      }}
    >
      {showEmptyState ? (
        <Typography p="16px" color="textSecondary">
          {t("hrTasklist.emptyState")}
        </Typography>
      ) : (
        tasks.map((task, index) => {
          const isLast = index === tasks.length - 1;
          return (
            <HrTaskListItem
              key={task.id}
              task={task}
              isLast={isLast}
              selected={selectedHrTaskId === task.id}
              handleSelectHrTask={handleSelectHrTask}
            />
          );
        })
      )}
      <LoadMoreButton onClick={handleFetchNext} cursor={cursor} />
    </List>
  );
};

export default HrTasklist;
